import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GroupPatternIcon, TagPatternIcon } from '@/lib/icon';
import { Text } from '@/lib/v2/components';
import { Divide, Flex } from '@/lib/v2/components/Layout/Stack';
import Select, { Option } from '@/lib/v2/components/Select/Select';

import { IGroup, IGroupList } from '@/src/ContactsModule/interfaces/Interfaces';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';

import { useGroup } from '@/modules/ContactsModule/hooks/useGroup';

const GroupsNamesBubbles = ({ value, deleteItem }: { value: string[]; deleteItem: Function }) => {
  const deleteValue = (index: number) => {
    deleteItem(index);
  };

  return (
    <div>
      {value.map((val, index) => (
        <span
          key={index}
          className="ml-0.5 inline-flex items-center rounded-full bg-blue-50 pl-2 pr-0.5
            text-base font-medium text-emblue-primary"
        >
          {val}
          <button
            className="ml-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full
                text-emblue-primary hover:bg-indigo-200 hover:text-emblue-primary-hover
                focus:bg-blue-400 focus:text-white focus:outline-none"
            type="button"
            onClick={() => {
              deleteValue(index);
            }}
          >
            <span className="sr-only">Remove small option</span>
            <svg className="h-2 w-2" fill="none" stroke="currentColor" viewBox="0 0 8 8">
              <path d="M1 1l6 6m0-6L1 7" strokeLinecap="round" strokeWidth="1.5" />
            </svg>
          </button>
        </span>
      ))}
    </div>
  );
};

export const ContactsTagsAndGroups = ({
  selectedTags,
  setSelectedTags,
  selectedGroups,
  setSelectedGroups,
  withTag,
}: {
  withTag?: boolean;
  selectedTags?: Option[];
  setSelectedTags?: Dispatch<SetStateAction<Option[]>>;
  selectedGroups: Option[];
  setSelectedGroups: Dispatch<SetStateAction<Option[]>>;
}) => {
  const service = useService();
  const [tagsOptions, setTagsOption] = useState<Option[]>();
  const [groupOptions, setGroupOptions] = useState<Option[]>();
  const { t } = useTranslation();
  const { createGroup } = useGroup();

  const getAndFormatTagsOptions = useCallback(async () => {
    const tags = await service?.getTagsDropDown({ identifier: 'TAGS_DROP_DOWN' });

    if (tags) {
      const tagListMapping: Option[] = tags.list.map((tag: { id: any; name: any }) => {
        return { id: tag.id, name: tag.name, value: tag.name } as Option;
      });
      setTagsOption(tagListMapping);
    }
  }, [service]);

  const getAndFormatGroupsOptions = useCallback(async () => {
    const groups: IGroupList = await service?.getGroups({ identifier: 'GROUPS_DROP_DOWN' });

    if (groups) {
      const groupsMapping: Option[] = groups.list
        .map(({ group }: { group: IGroup }) => {
          if (!group.id) return;
          return {
            id: group.id,
            name: group.groupName,
            value: group.groupName,
          } as Option;
        })
        .filter((group): group is Option => group !== undefined);
      setGroupOptions(groupsMapping);
      return groupsMapping;
    }
  }, [service]);

  useEffect(() => {
    void getAndFormatTagsOptions();
    void getAndFormatGroupsOptions();
  }, [getAndFormatTagsOptions, getAndFormatGroupsOptions]);

  const onHandleChangeTags = useCallback(
    (val: Option | Option[]) => {
      if (setSelectedTags) {
        if (Array.isArray(val) && val.length === 0) {
          setSelectedTags([{ id: 0, name: t('IMPORT_MAIN.noTags') } as Option]);
        } else if (Array.isArray(val)) {
          const filterTags = val.filter((tag) => tag.id !== 0);
          setSelectedTags(filterTags);
        } else {
          setSelectedTags([val]);
        }
      }
    },
    [setSelectedTags, t]
  );

  const onHandleChangeGroups = useCallback(
    (val: Option | Option[]) => {
      if (Array.isArray(val) && val.length === 0) {
        setSelectedGroups([]);
      } else if (Array.isArray(val)) {
        const filterGroups = val.filter((group) => group.id !== 0);
        setSelectedGroups(filterGroups);
      } else {
        setSelectedGroups([val]);
      }
    },
    [setSelectedGroups]
  );

  const onHandleCreateGroup = useCallback(
    async (value: string) => {
      const groupCreated = await createGroup(value);

      if (groupCreated) {
        const newOptions = await getAndFormatGroupsOptions();

        const newItem = newOptions?.find((item) => item.name === value) as Option;

        const data = [...selectedGroups, newItem];

        setSelectedGroups(data);
      }
    },
    [groupOptions, selectedGroups]
  );

  return (
    <div className="h-full w-full p-2">
      <Divide>
        <Flex column withGap gapSize="small" itemAlignment="start">
          {withTag && selectedTags && setSelectedTags && (
            <div className="w-full">
              <Flex column itemAlignment="start">
                <Text variant="headline">{t('IMPORT_MAIN.tags')}</Text>
              </Flex>
              <div className="w-full p-8">
                <Flex withGap gapSize="large" itemAlignment="start">
                  <Text as={'h1'} color="black" fontWeight="medium">
                    <div className="w-52">
                      <TagPatternIcon />
                    </div>
                  </Text>
                  <div className="max-w-xs">
                    <Text as="p">{t('IMPORT_MAIN.tagsDescription')}</Text>
                  </div>
                  <div className="w-full">
                    <Select
                      multiSelect
                      id="import-groups-select"
                      label={t('IMPORT_MAIN.tagSelectValueLabel')}
                      options={tagsOptions || []}
                      orderBy="asc"
                      placeholder={t('IMPORT_MAIN.tagSelectPlaceholder')}
                      value={selectedTags}
                      onChange={onHandleChangeTags}
                    />
                  </div>
                </Flex>
              </div>
            </div>
          )}
          <div className="w-full">
            <Flex column itemAlignment="start">
              <Text variant="headline">{t('IMPORT_MAIN.groups')}</Text>
            </Flex>
            <div className="w-full p-8">
              <Flex withGap gapSize="large" itemAlignment="start">
                <Text as={'h1'} color="black" fontWeight="medium">
                  <div className="w-52">
                    <GroupPatternIcon />
                  </div>
                </Text>
                <div className="max-w-xs text-justify">
                  <Text alignment="justify" as="p">
                    {t('IMPORT_MAIN.groupsDescription')}
                  </Text>
                </div>
                <div className="w-full">
                  <Select
                    multiSelect
                    withSearch
                    id="import-group-select"
                    label={t('IMPORT_MAIN.groupSelectValueLabel')}
                    options={groupOptions || []}
                    orderBy="asc"
                    placeholder={t('IMPORT_MAIN.groupSelectPlaceholder')}
                    searchPlaceholder={t('IMPORT_MAIN.search')}
                    titleCreate={t('IMPORT_MAIN.groupSelectTitleFallbackCreate')}
                    value={selectedGroups}
                    onChange={onHandleChangeGroups}
                    onCreate={onHandleCreateGroup}
                  />
                </div>
              </Flex>
            </div>
          </div>
        </Flex>
      </Divide>
    </div>
  );
};
